/**
 * Gate I8: Workflow Dashboard Types (Frontend)
 * 
 * These types mirror the server-side types for API response handling.
 * Keep in sync with apps/server/src/lib/workflow-types.ts
 */

export type Track = 
  | 'AVAILABLE'
  | 'AVAILABLE_BEHAVIOR'
  | 'AVAILABLE_BEH_MED'
  | 'UNAVAIL_BEHAVIOR'
  | 'UNAVAIL_BEH_MED'
  | 'UNAVAIL_MEDICAL';

export const VALID_TRACKS: Track[] = [
  'AVAILABLE',
  'AVAILABLE_BEHAVIOR',
  'AVAILABLE_BEH_MED',
  'UNAVAIL_BEHAVIOR',
  'UNAVAIL_BEH_MED',
  'UNAVAIL_MEDICAL'
];

export const TRACK_LABELS: Record<Track, string> = {
  'AVAILABLE': 'Available',
  'AVAILABLE_BEHAVIOR': 'Available (BEH)',
  'AVAILABLE_BEH_MED': 'Available (BEH+MED)',
  'UNAVAIL_BEHAVIOR': 'Unavailable (BEH)',
  'UNAVAIL_BEH_MED': 'Unavailable (BEH+MED)',
  'UNAVAIL_MEDICAL': 'Unavailable (MED)'
};

export type EvalStatus = 
  | 'INCOMPLETE'
  | 'NEEDS_REVIEW'
  | 'NEEDS_RECS'
  | 'COMPLETE';

export const VALID_EVAL_STATUSES: EvalStatus[] = [
  'INCOMPLETE',
  'NEEDS_REVIEW',
  'NEEDS_RECS',
  'COMPLETE'
];

export const EVAL_STATUS_LABELS: Record<EvalStatus, string> = {
  'INCOMPLETE': 'Incomplete',
  'NEEDS_REVIEW': 'Needs Review',
  'NEEDS_RECS': 'Needs Recs',
  'COMPLETE': 'Complete'
};

export type BucketKey = `${Track}:${EvalStatus}`;

export interface ConfirmStep {
  id: string;
  question: string;
  yesGuidance: string[];
  noGuidance: string[];
}

export interface GuidancePayloadMeta {
  kennelStatus: {
    code: string | null;
  };
}

export interface GuidancePayload {
  guidanceTitle: string;
  guidanceChecklist: string[];
  matchedRuleId: string;
  rationale: string;
  hasUnknownInputs: boolean;
  confirmSteps: ConfirmStep[];
  guidanceFlag?: string;
  guidanceBannerText?: string;
  rulesetVersion: string;
  meta: GuidancePayloadMeta;
}

export interface WorkflowDashboardItem {
  animalId: string;
  name: string;
  species: string;
  location: string;
  zoneCode: string | null;
  track: Track;
  evalStatus: EvalStatus;
  bucketKey: BucketKey;
  daysInTrack: number | null;
  daysInEvalStatus: number | null;
  daysAvailable: number | null;
  daysSinceLastInteraction: number | null;
  isOnHold: boolean;
  hasCiBq: boolean | null;
  hasAttentionFlag: boolean;
  photoUrl: string | null;
  adoptionLevel: string | null;
  intakeDate: string | null;
  evalActivated: boolean;
  evalDone: number;
  evalTotal: number;
  guidance: GuidancePayload;
}

export interface ExcludedAnimal {
  animalId: string;
  name: string | null;
}

export interface WorkflowDashboardResponse {
  enabled: boolean;
  items: WorkflowDashboardItem[];
  meta: {
    totalCount: number;
    bucketCounts: Record<string, number>;
    missingDataCount: number;
    excludedAnimals: ExcludedAnimal[];
    appliedFilters: {
      location: string | null;
      species: string | null;
    };
    rulesetVersion: string;
    computedAt: string;
  };
}

export function buildBucketKey(track: Track, evalStatus: EvalStatus): BucketKey {
  return `${track}:${evalStatus}`;
}
